type MarkKind = 'open' | 'close' | 'none';

interface QuoteFamily {
  /** Marks that can open a whole-quote pair. */
  opens: string;
  /** Marks that can close a whole-quote pair. */
  closes: string;
  /** How a mark of the family inside the quote reads at `index`. */
  kindAt: (text: string, index: number) => MarkKind;
  /** A closing mark typed twice at the end is a typo, so the extra one is dropped. */
  collapsesRepeatedClose: boolean;
}

const LETTER = new RegExp(String.raw`\p{L}`, 'u');
const LETTER_OR_DIGIT = new RegExp(String.raw`[\p{L}\d]`, 'u');
const DIGIT = /\d/;
const PRIMES = '\u2019"'; // ’ "
const SPACE = /\s/;
// A mark right after an opening mark opens too, so a doubled opening mark (““…) reads as two.
const OPENING_CONTEXT = /[([{\u2014\u2013\u201C\u201E\u2018\u201A\u00AB\u2039\u300C\u300E-]/;
const CLOSING_CONTEXT = /[.,;:!?)\]}\u2014\u2013-]/;

const isEdgeOrSpace = (char: string | undefined): boolean => char === undefined || SPACE.test(char);

/**
 * Whether a mark opens or closes, read from its neighbours, since curly marks swap roles between
 * languages (“ closes in German) and straight `"` has no direction: after a space or bracket and
 * before text it opens, after text and before a space or punctuation it closes. A `"` or ’ right
 * after a digit is an inch mark or prime (`65" TVs`, `6’ shelf`), not a quotation mark.
 */
const kindFromContext = (text: string, index: number): MarkKind => {
  const before = text[index - 1];
  if (PRIMES.includes(text[index]) && DIGIT.test(before ?? '')) return 'none';
  const after = text[index + 1];
  if ((isEdgeOrSpace(before) || OPENING_CONTEXT.test(before)) && !isEdgeOrSpace(after)) return 'open';
  if (!isEdgeOrSpace(before) && (isEdgeOrSpace(after) || CLOSING_CONTEXT.test(after))) return 'close';
  return 'none';
};

/** A fixed opening and closing character, for marks whose direction never changes. */
const directional =
  (open: string, close: string) =>
  (text: string, index: number): MarkKind => {
    if (text[index] === open) return 'open';
    if (text[index] === close) return 'close';
    return 'none';
  };

/**
 * The quotation marks that can wrap a whole quote in the languages Poly serves. Double marks are
 * one family, so a pair typed with mixed marks (“…" or „…“) still counts as a pair.
 */
const FAMILIES: readonly QuoteFamily[] = [
  {
    opens: '\u201C\u201D\u201E"', // “ ” „ "
    closes: '\u201D\u201C"', // ” “ "
    kindAt: kindFromContext,
    collapsesRepeatedClose: true,
  },
  {
    opens: '\u2018\u201A', // ‘ ‚
    closes: '\u2019\u2018', // ’ ‘
    // ’ after a letter is an apostrophe (We’re, sellers’), never a closing mark.
    kindAt: (text, index) =>
      text[index] === '\u2019' && LETTER.test(text[index - 1] ?? '') ? 'none' : kindFromContext(text, index),
    collapsesRepeatedClose: false,
  },
  { opens: '\u00AB', closes: '\u00BB', kindAt: directional('\u00AB', '\u00BB'), collapsesRepeatedClose: false },
  { opens: '\u00BB', closes: '\u00AB', kindAt: directional('\u00BB', '\u00AB'), collapsesRepeatedClose: false },
  { opens: '\u2039', closes: '\u203A', kindAt: directional('\u2039', '\u203A'), collapsesRepeatedClose: false },
  { opens: '\u300C', closes: '\u300D', kindAt: directional('\u300C', '\u300D'), collapsesRepeatedClose: false },
  { opens: '\u300E', closes: '\u300F', kindAt: directional('\u300E', '\u300F'), collapsesRepeatedClose: false },
];

const familyMarks = (family: QuoteFamily): string => family.opens + family.closes;

/**
 * How many marks of `family` the text leaves open, or -1 if a mark closes one that was never
 * opened.
 */
const openDepth = (text: string, family: QuoteFamily): number => {
  const marks = familyMarks(family);
  let depth = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (marks.includes(text[i])) {
      const kind = family.kindAt(text, i);
      if (kind === 'open') depth += 1;
      if (kind === 'close') {
        depth -= 1;
        if (depth < 0) return -1;
      }
    }
  }
  return depth;
};

/**
 * The text inside one wrapping pair of `family`, or `undefined` if the family doesn't wrap it.
 * The pair wraps the text when nothing inside closes the leading mark early and every mark opened
 * inside is closed, so `“Day one” is how we think, and so is “customer obsession”` keeps its
 * marks. For double marks, a run of closing marks at the end keeps only as many as close quotes
 * opened inside, so a closing mark typed twice (`“…””`) loses the extra one. The run is counted
 * once, so the work stays linear however long it is.
 */
const unwrap = (text: string, family: QuoteFamily): string | undefined => {
  if (text.length < 2 || !family.opens.includes(text[0]) || !family.closes.includes(text[text.length - 1])) {
    return undefined;
  }
  if (!family.collapsesRepeatedClose) {
    const inner = text.slice(1, -1);
    return openDepth(inner, family) === 0 ? inner : undefined;
  }
  const last = text[text.length - 1];
  let end = text.length - 1;
  while (end > 1 && text[end - 1] === last) end -= 1;
  const inner = text.slice(1, end);
  const depth = openDepth(inner, family);
  if (depth < 0 || text.length - end < depth + 1) return undefined;
  return inner + last.repeat(depth);
};

/**
 * Whether the mark at the start opens a quote that nothing in the text closes, as when an author
 * types `“Growth came fast.` with no closing mark.
 */
const hasUnpairedOpening = (text: string, family: QuoteFamily): boolean => {
  if (!family.opens.includes(text[0]) || family.kindAt(text, 0) !== 'open') return false;
  const marks = familyMarks(family);
  let depth = 1;
  for (let i = 1; i < text.length; i += 1) {
    if (marks.includes(text[i])) {
      const kind = family.kindAt(text, i);
      if (kind === 'open') depth += 1;
      if (kind === 'close') {
        depth -= 1;
        if (depth === 0) return false;
      }
    }
  }
  return true;
};

/** Whether the mark at the end closes a quote that nothing in the text opened. */
const hasUnpairedClosing = (text: string, family: QuoteFamily): boolean => {
  const last = text.length - 1;
  if (!family.closes.includes(text[last]) || family.kindAt(text, last) !== 'close') return false;
  const marks = familyMarks(family);
  let depth = 0;
  for (let i = 0; i < last; i += 1) {
    if (marks.includes(text[i])) {
      const kind = family.kindAt(text, i);
      if (kind === 'open') depth += 1;
      if (kind === 'close') depth = Math.max(0, depth - 1);
    }
  }
  return depth === 0;
};

/** A leading ‘ before a letter or digit may be an elision (‘90s, ‘Tis) rather than an opening mark. */
const startsWithElision = (text: string): boolean => text[0] === '\u2018' && LETTER_OR_DIGIT.test(text[1] ?? '');

/** A trailing ’ or `"` after a digit may be a prime or inch mark (2020’, 65") rather than a closing mark. */
const endsWithPrime = (text: string): boolean => {
  const last = text.length - 1;
  return PRIMES.includes(text[last]) && DIGIT.test(text[last - 1] ?? '');
};

/**
 * The text without a mark at either end that has no partner, or `undefined` if there is none. An
 * unpaired edge mark that may belong to the word it touches is kept, since removing it would
 * change the author's words.
 */
const dropUnpairedEdge = (text: string): string | undefined => {
  if (text.length === 0) return undefined;
  if (!startsWithElision(text) && FAMILIES.some((family) => hasUnpairedOpening(text, family))) {
    return text.slice(1);
  }
  if (!endsWithPrime(text) && FAMILIES.some((family) => hasUnpairedClosing(text, family))) {
    return text.slice(0, -1);
  }
  return undefined;
};

// Real quotes are wrapped in at most a couple of layers of marks. The cap keeps pathological
// content (thousands of nested marks) from making rendering quadratic.
const MAX_PASSES = 8;

const QUOTE_MARKS_ATTRIBUTE = 'data-poly-quote-marks';

/**
 * Ref callback for an element that shows a quote stripped by `stripQuoteMarks`. It sets
 * `data-poly-quote-marks` on the live element so the CSS marks appear. Hydrating HTML published
 * before the attribute existed replaces its typed marks with the stripped text, but Preact's
 * hydrate doesn't add attributes, so without this the quote would show no marks at all.
 */
export const markQuoteElement = (element: Element | null): void => {
  element?.setAttribute(QUOTE_MARKS_ATTRIBUTE, '');
};

/**
 * Removes the quotation marks an author or translator typed around a whole quote. Components
 * that display a quote add the marks themselves with CSS, in the style of the page's language,
 * so marks left around the content would show twice.
 *
 * A pair is removed only when it wraps the whole text, so a quote that starts or ends with a
 * quoted phrase keeps it (`“Day one” is how we think.`), and apostrophes stay (`We’re`,
 * `It changed our sellers’`). Mixed double marks (`“…"`), a closing mark typed twice (`“…””`),
 * nested pairs (`“« … »”`) and the spaces French puts inside guillemets are removed too, as is
 * a mark at the start or end with no partner in the text (`“Growth came fast.`). Inch marks and
 * primes inside the quote are kept (`“We sell 65" TVs”` becomes `We sell 65" TVs`), as are an
 * unpaired leading elision (`‘90s`, `‘Tis`) and an unpaired trailing prime or inch mark (`2020’`,
 * `65"`).
 *
 * @param quote - Authored quote text.
 * @returns The text without wrapping quotation marks or surrounding spaces.
 */
export const stripQuoteMarks = (quote: string): string => {
  let text = quote.trim();
  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const current = text;
    const inner =
      FAMILIES.map((family) => unwrap(current, family)).find((value) => value !== undefined) ??
      dropUnpairedEdge(current);
    if (inner === undefined) return text;
    text = inner.trim();
  }
  return text;
};
